import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CompassIcon as Compass,
  XIcon as X,
  ArrowRightIcon as ArrowRight,
} from "@phosphor-icons/react";

/**
 * Persona-aware "Your toolkit" card — a one-time orientation map so
 * every persona knows WHERE their features live (calendar, availability,
 * bookings, payouts…) PLUS a "worth exploring" row of cross-discovery
 * links so nobody stays stuck in their own lane (The Dog's Voice,
 * Dog Shops, Events…). Pure static content keyed by persona; dismissed
 * state persists per persona in localStorage.
 */
const TOOLKITS = {
  service_provider: [
    { to: "/dashboard", label: "Appointments calendar", desc: "Confirmed bookings appear on your dashboard calendar automatically." },
    { to: "/become-a-provider", label: "Weekly availability", desc: "Set the hours clients can book — slots outside them are never offered." },
    { to: "/my-bookings", label: "My Bookings", desc: "Complete, cancel, or message clients about any appointment." },
    { to: "/settings", label: "Vacation mode", desc: "Pause new bookings in Settings → Availability when you're away." },
  ],
  transporter: [
    { to: "/transporter", label: "Dispatch board", desc: "Open bid invitations and your active jobs live here." },
    { to: "/dashboard", label: "Pickup & delivery calendar", desc: "Assigned runs appear on your dashboard calendar with dates." },
    { to: "/transport/getting-started", label: "How bidding works", desc: "The full guide to quoting and winning transport jobs." },
  ],
  storefront_seller: [
    { to: "/me/store", label: "My Store", desc: "Your shop page, products, and story — all managed here." },
    { to: "/me/store?tab=orders", label: "Orders", desc: "New orders land here; mark them fulfilled as you ship." },
    { to: "/settings", label: "Vacation mode", desc: "Pause your shop in Settings → Availability when you're away." },
  ],
  buyer: [
    { to: "/marketplace", label: "Dog Marketplace", desc: "Every listed dog and litter, with scam-proof escrow checkout." },
    { to: "/breeders", label: "Kennel directory", desc: "Heart a kennel to get alerts when they post new litters." },
    { to: "/escrow/my", label: "My Escrow", desc: "Track every payment — funds release only when your puppy arrives." },
  ],
  dog_owner: [
    { to: "/dog-log", label: "Dog Log", desc: "Meals, walks, meds — one tap each, streaks included." },
    { to: "/dogs", label: "Care Passport", desc: "Each dog's shareable care record lives on their profile." },
    { to: "/dashboard", label: "Calendar", desc: "Birthdays and health reminders appear here automatically." },
  ],
  breeder: [
    { to: "/litters", label: "My Litters", desc: "Register litters and manage puppy buyers in one place." },
    { to: "/dashboard", label: "Breeding calendar", desc: "Heat cycles, due dates, and go-home dates, auto-plotted." },
    { to: "/breeders?create=1", label: "Kennel page", desc: "Your public, Google-indexable kennel profile." },
    { to: "/payouts", label: "Payouts", desc: "Stripe setup and every payout you've received." },
  ],
};

/** Cross-discovery: features OUTSIDE each persona's lane that they'd
 *  love but would never find on their own. */
const EXPLORE = {
  service_provider: [
    { to: "/feed", label: "The Dog's Voice", desc: "Post before/after shots of your work — clients follow and book from the feed." },
    { to: "/storefronts", label: "Dog Shops", desc: "Sell your own products (shampoo, bandanas, treats) alongside your services." },
    { to: "/events", label: "Events", desc: "Meetups and competitions near you — great for meeting new clients." },
  ],
  transporter: [
    { to: "/feed", label: "The Dog's Voice", desc: "Share safe-arrival photos — happy deliveries are your best advertising." },
    { to: "/services", label: "Service directory", desc: "See how groomers and trainers present themselves — reviews win jobs." },
  ],
  storefront_seller: [
    { to: "/feed", label: "The Dog's Voice", desc: "Show your products in action — feed posts drive shop visits." },
    { to: "/events", label: "Events", desc: "Meetups near you — vendor tables sell product." },
  ],
  buyer: [
    { to: "/feed", label: "The Dog's Voice", desc: "The community feed — follow kennels and watch litters grow up." },
    { to: "/services", label: "Book a pro", desc: "Groomers, trainers, walkers, and sitters for when your puppy comes home." },
    { to: "/storefronts", label: "Dog Shops", desc: "Gear, treats, and merch from sellers across the registry." },
    { to: "/transport", label: "Puppy transport", desc: "Licensed transporters bid to bring your puppy safely to your door." },
  ],
  dog_owner: [
    { to: "/feed", label: "The Dog's Voice", desc: "Share your dog's moments — the community feed for every dog lover." },
    { to: "/services", label: "Book a pro", desc: "Groomers, trainers, walkers, and sitters — booked and paid in-app." },
    { to: "/storefronts", label: "Dog Shops", desc: "Gear, treats, and merch from sellers across the registry." },
    { to: "/events", label: "Events", desc: "Meetups, shows, and competitions near you." },
  ],
  breeder: [
    { to: "/feed", label: "The Dog's Voice", desc: "Post puppy updates — buyers follow kennels and inquire from the feed." },
    { to: "/storefronts", label: "Open a Dog Shop", desc: "Sell merch, starter kits, or supplies alongside your litters." },
    { to: "/events", label: "Events", desc: "Shows and meetups — list your own to meet buyers in person." },
  ],
};

export default function PersonaToolkitCard({ persona }) {
  const items = TOOLKITS[persona];
  const explore = EXPLORE[persona] || [];
  const storageKey = `toolkit-dismissed-${persona}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  if (!items || dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };
  return (
    <div className="relative rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-5 sm:p-6 mb-8" data-testid="persona-toolkit-card">
      <div className="flex items-center gap-2 mb-3">
        <Compass size={18} weight="duotone" className="text-gold-dark" />
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8A6A00]">
          Your toolkit — where everything lives
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {items.map((it) => (
          <Link
            key={it.label}
            to={it.to}
            className="group flex items-start gap-2 rounded-lg -m-1 p-1.5 hover:bg-white/70 transition-colors"
            data-testid={`toolkit-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <ArrowRight size={13} weight="bold" className="mt-1 flex-shrink-0 text-gold-dark group-hover:text-crimson transition-colors" />
            <span className="text-[12.5px] leading-snug">
              <span className="font-bold text-ink group-hover:text-crimson transition-colors">{it.label}</span>
              <span className="text-[#5A5A5A]"> — {it.desc}</span>
            </span>
          </Link>
        ))}
      </div>
      {explore.length > 0 && (
        <>
          <div className="flex items-center gap-2 mt-5 mb-3 pt-4 border-t border-[#E8D9A6]/60">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8A6A00]">
              Worth exploring — beyond your lane
            </p>
          </div>          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="toolkit-explore-section">
            {explore.map((it) => (
              <Link
                key={it.label}
                to={it.to}
                className="group flex items-start gap-2 rounded-lg -m-1 p-1.5 hover:bg-white/70 transition-colors"
                data-testid={`explore-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <ArrowRight size={13} weight="bold" className="mt-1 flex-shrink-0 text-gold-dark group-hover:text-crimson transition-colors" />
                <span className="text-[12.5px] leading-snug">
                  <span className="font-bold text-ink group-hover:text-crimson transition-colors">{it.label}</span>
                  <span className="text-[#5A5A5A]"> — {it.desc}</span>
                </span>
              </Link>
            ))}
          </div>
        </>
      )}
      <p className="mt-4 pt-3 border-t border-[#E8D9A6]/60 text-[11.5px] text-[#6B6B6B]" data-testid="toolkit-multihat-note">
        Wear more than one hat? Breed <em>and</em> groom? You can switch or add a
        role anytime in{" "}
        <Link to="/settings" className="font-semibold underline text-ink hover:text-crimson transition-colors">
          Settings
        </Link>
        .
      </p>
      <button
        onClick={dismiss}
        aria-label="Dismiss toolkit"
        className="absolute top-3 right-3 h-7 w-7 rounded-full hover:bg-black/5 flex items-center justify-center text-[#9A9A9A] hover:text-ink transition-colors"
        data-testid="toolkit-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
